import { Outlet, useLocation } from "react-router"
import { productRouteSlug } from "@exxatdesignux/product-framework/product-routing"

import { ScopeChooser } from "@/components/scope-chooser"
import { DashboardLoadingFallback } from "@/src/pages/_dashboard-loading"
import { useProduct } from "@/contexts/product-context"
import { useActiveScope } from "@/hooks/use-active-scope"
import { useAppStore } from "@/stores/app-store"
import type { Product } from "@/stores/app-store"

const COMPLIANCE_DEMO_SUFFIXES = ["/dashboard", "/student", "/faculty"] as const

const PRISM_DEMO_HUB_SUFFIXES = [
  "/dashboard",
  "/courses",
  "/students",
  "/program-details",
  "/competency-management",
] as const

function isUnderProductRoot(
  product: Product,
  pathname: string,
  customProducts: ReturnType<typeof useProduct>["customProducts"],
  activeCustomIndex: number,
): boolean {
  const slug = productRouteSlug(product, customProducts, activeCustomIndex)
  return pathname === `/${slug}` || pathname.startsWith(`/${slug}/`)
}

/** Demo iframe routes that should not block on program scope. */
function skipsScopeGate(
  product: Product,
  pathname: string,
  customProducts: ReturnType<typeof useProduct>["customProducts"],
  activeCustomIndex: number,
): boolean {
  if (!isUnderProductRoot(product, pathname, customProducts, activeCustomIndex)) {
    return false
  }

  if (product === "exxat-compliance") {
    return COMPLIANCE_DEMO_SUFFIXES.some(suffix => pathname.endsWith(suffix))
  }
  if (product === "exxat-student-success") {
    return pathname.endsWith("/competency-management") || pathname.endsWith("/dashboard")
  }
  if (product === "exxat-curriculum-mapping") {
    return pathname.endsWith("/dashboard") || pathname.endsWith("/curriculum-mapping")
  }
  if (product === "exxat-prism" || product === "exxat-custom") {
    return PRISM_DEMO_HUB_SUFFIXES.some(suffix => pathname.endsWith(suffix))
  }
  if (product === "exxat-one-sites" || product === "exxat-surveys") {
    return (
      pathname.endsWith("/launch") ||
      pathname.endsWith("/dashboard") ||
      pathname.endsWith("/surveys-dashboard")
    )
  }
  return false
}

export function RequireProductScope() {
  const { product, customProducts, activeCustomIndex } = useProduct()
  const { pathname } = useLocation()
  const switching = useAppStore(s => s.productSwitching)
  const { status } = useActiveScope(product)

  if (switching) return <DashboardLoadingFallback />
  if (
    status === "open" ||
    skipsScopeGate(product, pathname, customProducts, activeCustomIndex)
  ) {
    return <Outlet />
  }
  return <ScopeChooser product={product} />
}
